import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getSupabase } from '../lib/supabase';
import SmartdoxxLogo from '../components/SmartdoxxLogo';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';

  const handleDemoLogin = async () => {
    setError(null);
    setDemoLoading(true);

    try {
      const supabase = getSupabase();
      if (!supabase) throw new Error('Supabase not configured');

      const existingToken = localStorage.getItem('demo_session_token');

      if (existingToken) {
        const { data: resumeData, error: resumeError } = await supabase.functions.invoke(
          'resume-demo-session',
          { body: { session_token: existingToken } }
        );

        if (!resumeError && resumeData?.status === 'active') {
          const { error: signInError } = await signIn(resumeData.email, resumeData.password);
          if (signInError) throw signInError;
          navigate(redirectTo);
          return;
        }

        localStorage.removeItem('demo_session_token');
        localStorage.removeItem('demo_expires_at');
      }

      const { data, error: createError } = await supabase.functions.invoke('create-demo-session', {
        body: { user_agent: navigator.userAgent }
      });

      if (createError) throw createError;
      if (data?.error) throw new Error(data.message || 'Demo unavailable');

      localStorage.setItem('demo_session_token', data.session_token);
      localStorage.setItem('demo_expires_at', data.expires_at);

      const { error: signInError } = await signIn(data.email, data.password);
      if (signInError) throw signInError;
      navigate(redirectTo);

    } catch (err: any) {
      setError(err.message || 'Failed to start demo');
      setDemoLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) throw error;
      navigate(redirectTo);
    } catch (err: any) {
      setError(err.message || 'Failed to sign in');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-page-background">
      <div className="w-full max-w-md mx-4">
        <div className="bg-card rounded-lg shadow-sm p-8">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <SmartdoxxLogo size="2xl" showText={false} />
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold text-foreground mb-1">Welcome to Smartviewer</h1>
            <p className="text-muted-foreground text-sm">Sign in to your account</p>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-destructive/10 border border-destructive/20 p-3">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-destructive mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-destructive">{error}</div>
              </div>
            </div>
          )}

          <form className="space-y-4" onSubmit={handleLogin}>
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition text-sm bg-background text-foreground"
                placeholder="Enter your email"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-foreground mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 pr-12 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition text-sm bg-background text-foreground"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-gradient text-primary-foreground font-medium py-2.5 px-4 rounded-md transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm hover:opacity-90"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-foreground" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-4 text-center">
            <p className="text-sm text-muted-foreground">
              {"Don't have an account? "}
              <Link to="/auth/signup" className="font-medium text-primary hover:opacity-80 transition">
                Register here
              </Link>
            </p>
          </div>

          {/* Demo Account Button */}
          <div className="mt-4">
            <button
              type="button"
              onClick={handleDemoLogin}
              disabled={demoLoading || loading}
              className="w-full border border-border text-foreground font-medium py-2.5 px-4 rounded-md hover:bg-accent transition text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {demoLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading demo...
                </>
              ) : (
                'View Demo Account'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}